import { Injectable, inject } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

export interface SEOConfig {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article' | 'event';
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
}

@Injectable({ providedIn: 'root' })
export class SeoService {
  private readonly meta = inject(Meta);
  private readonly titleService = inject(Title);
  private readonly router = inject(Router);

  private readonly defaultConfig = {
    title: 'REDAL - Conectando empresários, Impulsionando negócios',
    description: 'Junte-se a uma comunidade exclusiva de empresários e empreendedores comprometidos com o crescimento mútuo e oportunidades de negócios.',
    keywords: 'evento corporativos, networking empresarial, palestras exclusivas, compartilhamento de conhecimento, oportunidade de negócios, comunidade empresarial, rede de conexão, Maceió, Alagoas',
    image: `${environment.siteUrl}/redal_logo.png`,
    url: environment.siteUrl,
    type: 'website' as const,
  };

  constructor() {
    // Atualiza canonical URL automaticamente em mudanças de rota
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.updateCanonicalUrl(window.location.href);
      });

    // Configura robots meta tag baseado no ambiente
    this.setRobotsMetaTag();
  }

  /**
   * Define a meta tag robots baseado no ambiente (dev/prod)
   * DESENVOLVIMENTO: noindex, nofollow (não indexa)
   * PRODUÇÃO: index, follow (indexa normalmente)
   */
  private setRobotsMetaTag(): void {
    const robotsContent = environment.production ? 'index, follow' : 'noindex, nofollow';
    this.meta.updateTag({ name: 'robots', content: robotsContent });
    
    // Log para debug (apenas em dev)
    if (!environment.production) {
      console.log('🚫 SEO: Ambiente de DESENVOLVIMENTO - Indexação BLOQUEADA');
      console.log('   robots: noindex, nofollow');
    }
  }

  /**
   * Configura todas as meta tags de SEO
   */
  updateMetaTags(config: SEOConfig): void {
    const seoConfig = { ...this.defaultConfig, ...config };

    // Define o título da página
    const fullTitle = config.title
      ? `${config.title} | REDAL`
      : this.defaultConfig.title;
    this.titleService.setTitle(fullTitle);

    // Meta tags básicas
    this.meta.updateTag({ name: 'description', content: seoConfig.description });
    this.meta.updateTag({ name: 'keywords', content: seoConfig.keywords });
    this.meta.updateTag({ name: 'author', content: 'REDAL - Comunidade Empresarial' });
    
    // IMPORTANTE: Em desenvolvimento, bloqueia indexação pelo Google
    const robotsContent = environment.production ? 'index, follow' : 'noindex, nofollow';
    this.meta.updateTag({ name: 'robots', content: robotsContent });

    // Open Graph (Facebook, LinkedIn, WhatsApp)
    this.meta.updateTag({ property: 'og:site_name', content: 'REDAL' });
    this.meta.updateTag({ property: 'og:title', content: fullTitle });
    this.meta.updateTag({ property: 'og:description', content: seoConfig.description });
    this.meta.updateTag({ property: 'og:type', content: seoConfig.type });
    this.meta.updateTag({ property: 'og:url', content: seoConfig.url || this.defaultConfig.url });
    this.meta.updateTag({ property: 'og:image', content: seoConfig.image });
    this.meta.updateTag({ property: 'og:image:alt', content: fullTitle });
    this.meta.updateTag({ property: 'og:image:width', content: '1200' });
    this.meta.updateTag({ property: 'og:image:height', content: '630' });
    this.meta.updateTag({ property: 'og:locale', content: 'pt_BR' });

    // Twitter Cards
    this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
    this.meta.updateTag({ name: 'twitter:title', content: fullTitle });
    this.meta.updateTag({ name: 'twitter:description', content: seoConfig.description });
    this.meta.updateTag({ name: 'twitter:image', content: seoConfig.image });
    this.meta.updateTag({ name: 'twitter:image:alt', content: fullTitle });

    // Article meta tags (se aplicável)
    if (seoConfig.type === 'article' || seoConfig.type === 'event') {
      if (seoConfig.author) {
        this.meta.updateTag({ property: 'article:author', content: seoConfig.author });
      }
      if (seoConfig.publishedTime) {
        this.meta.updateTag({ property: 'article:published_time', content: seoConfig.publishedTime });
      }
      if (seoConfig.modifiedTime) {
        this.meta.updateTag({ property: 'article:modified_time', content: seoConfig.modifiedTime });
      }
    }

    // Geo tags para localização
    this.meta.updateTag({ name: 'geo.region', content: 'BR-AL' });
    this.meta.updateTag({ name: 'geo.placename', content: 'Maceió' });

    // Update canonical URL
    this.updateCanonicalUrl(seoConfig.url || this.defaultConfig.url);
  }

  /**
   * Atualiza a URL canônica
   */
  private updateCanonicalUrl(url: string): void {
    let link: HTMLLinkElement | null = document.querySelector('link[rel="canonical"]');
    
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    
    link.setAttribute('href', url);
  }

  /**
   * Adiciona JSON-LD Schema para SEO estruturado
   */
  addJsonLdSchema(schema: any): void {
    let script: HTMLScriptElement | null = document.querySelector('script[type="application/ld+json"]');
    
    if (!script) {
      script = document.createElement('script');
      script.setAttribute('type', 'application/ld+json');
      document.head.appendChild(script);
    }
    
    script.textContent = JSON.stringify(schema);
  }

  /**
   * Schema para Organização
   */
  setOrganizationSchema(): void {
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'REDAL',
      alternateName: 'REDAL - Comunidade Empresarial',
      url: environment.siteUrl,
      logo: `${environment.siteUrl}/redal_logo.png`,
      description: 'Comunidade de empresários comprometidos com crescimento mútuo e oportunidades de negócios.',
      slogan: 'Conectando empresários, Impulsionando negócios',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Maceió',
        addressRegion: 'AL',
        addressCountry: 'BR',
      },
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'customer service',
        availableLanguage: ['Portuguese'],
      },
      sameAs: [
        'https://www.instagram.com/somosredal/',
        environment.siteUrl,
      ],
      areaServed: {
        '@type': 'Country',
        name: 'Brasil',
      },
    };

    this.addJsonLdSchema(schema);
  }

  /**
   * Schema para Evento
   */
  setEventSchema(event: {
    name: string;
    description: string;
    startDate: string;
    location: string;
    image?: string;
    url?: string;
  }): void {
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'Event',
      name: event.name,
      description: event.description,
      startDate: event.startDate,
      location: {
        '@type': 'Place',
        name: event.location,
        address: {
          '@type': 'PostalAddress',
          addressLocality: 'Maceió',
          addressRegion: 'AL',
          addressCountry: 'BR',
        },
      },
      image: event.image || `${environment.siteUrl}/redal_logo.png`,
      url: event.url || environment.siteUrl,
      organizer: {
        '@type': 'Organization',
        name: 'REDAL',
        url: environment.siteUrl,
      },
    };

    this.addJsonLdSchema(schema);
  }

  /**
   * Schema para Breadcrumb
   */
  setBreadcrumbSchema(items: { name: string; url: string }[]): void {
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: items.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.name,
        item: item.url,
      })),
    };

    this.addJsonLdSchema(schema);
  }

  /**
   * Remove todas as meta tags personalizadas e volta ao padrão
   */
  resetToDefault(): void {
    this.updateMetaTags({});
    this.setOrganizationSchema();
  }
}

